import React, { useMemo } from 'react';
import {
    Star,
    User,
    Calendar,
    CalendarCheck,
    ClipboardList,
    Check,
    BadgeCheck,
    X,
    ClipboardPen,
    Home,
    MessageCircle,
    Milestone,
    Sparkles,
    UserPen,
} from 'lucide-react';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';

import { FlexColumn, Flex } from 'src/components/flex';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import type { ILead, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import { ELeadStage } from 'src/services/api/leads';
import type { IUser } from 'src/api/users-api';
import type { TStageOrigin } from 'src/components/lead-sidebar/lead-stage-timeline-events';
import { buildTimelineEvents } from 'src/components/lead-sidebar/lead-stage-timeline-events';

import { useAuthenticatedState } from 'src/authenticated-state/context';

import styles from 'src/components/lead-sidebar/LeadStageTimeline.module.css';

const STAGE_ICONS: Record<ELeadStage, React.ReactNode> = {
    [ELeadStage.NEW]: <Star size={14} />,
    [ELeadStage.CONTACTED]: <User size={14} />,
    [ELeadStage.RESPONSIVE]: <MessageCircle size={14} />,
    [ELeadStage.TOUR_REQUESTED]: <Calendar size={14} />,
    [ELeadStage.TOUR_SCHEDULED]: <Calendar size={14} />,
    [ELeadStage.TOUR_COMPLETED]: <CalendarCheck size={14} />,
    [ELeadStage.INVITED_TO_APPLY]: <ClipboardList size={14} />,
    [ELeadStage.APPLICATION_COMPLETED]: <Check size={14} />,
    [ELeadStage.APPLICATION_APPROVED]: <BadgeCheck size={14} />,
    [ELeadStage.LEASE_SIGNED]: <ClipboardPen size={14} />,
    [ELeadStage.NOT_INTERESTED]: <X size={14} />,
    [ELeadStage.NOT_QUALIFIED]: <X size={14} />,
};

const UNKNOWN_STAGE_ICON = <Milestone size={14} />;

const NEGATIVE_STAGES: string[] = [ELeadStage.NOT_QUALIFIED, ELeadStage.NOT_INTERESTED];

function OriginPill({ origin, label, details }: {
    origin: TStageOrigin | null,
    label: string | null,
    details: string | null,
}) {
    if (!origin) {
        return null;
    }

    return (
        <>
            <span className={styles.originSeparator}>•</span>
            <Tooltip title={details}>
                <span
                    className={clsx(
                        styles.originPill,
                        origin === 'manual' ? styles.originPillManual : styles.originPillAuto
                    )}
                    data-testid={`timeline-origin-${origin}`}
                >
                    {origin === 'manual' ? <UserPen size={11} /> : <Sparkles size={11} />}
                    <Typography variant="caption" color="inherit" className={styles.originPillLabel}>
                        {label}
                    </Typography>
                </span>
            </Tooltip>
        </>
    );
}

export default function LeadStageTimeline({
    lead,
    stages,
    interests,
    teamMembers,
    isExpanded = true,
}: {
    lead: ILead,
    stages: IStageHistory[],
    interests: ILeadPropertyInterest[],
    teamMembers: IUser[],
    isExpanded?: boolean,
}) {
    const { userSettings } = useAuthenticatedState();
    const leadOwner = teamMembers.find(member => member.id === lead.owner);

    const timelineEvents = useMemo(() => buildTimelineEvents({
        lead,
        stages,
        interests,
        leadOwnerName: leadOwner?.name ?? 'Unknown',
        timezone: userSettings.timezone_override,
    }), [lead, stages, interests, leadOwner?.name, userSettings.timezone_override]);

    return (
        <ExpandableItem title="Timeline" defaultExpanded={isExpanded}>
            <div style={{ position: 'relative', paddingLeft: 48 }}>
                <FlexColumn gap={1}>
                    {timelineEvents.map((event, index) => {
                        const isLastEvent = index === timelineEvents.length - 1;

                        if (event.kind === 'interest') {
                            const { interest, timeValue, origin, originLabel, originDetails } = event;
                            return (
                                <Flex key={event.key} alignItems="flex-start" style={{ position: 'relative' }}>
                                    {!isLastEvent && (
                                        <div className={styles.timelineLine} />
                                    )}
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: '#3B82F6',
                                            color: '#fff',
                                            position: 'absolute',
                                            left: -48,
                                        }}
                                    >
                                        <Home size={14} />
                                    </Avatar>
                                    <FlexColumn gap={0} sx={{ minWidth: 0 }}>
                                        <div className={styles.eventLabel}>Property interest</div>
                                        <Tooltip title={`${lead.name} showed interest in ${interest.property_name}`}>
                                            <Typography className={clsx(styles.eventDetails, 'ellipsisText')} variant="body2" color="textSecondary">
                                                {lead.name} showed interest in {interest.property_name}
                                            </Typography>
                                        </Tooltip>
                                        <Flex alignItems="center" className={styles.eventTimestamp}>
                                            <Calendar size={16} color="#6B7280" />
                                            {timeValue}
                                            <OriginPill origin={origin} label={originLabel} details={originDetails} />
                                        </Flex>
                                    </FlexColumn>
                                </Flex>
                            );
                        }

                        const { stageValue, label, details, timeValue, origin, originLabel, originDetails } = event;

                        return (
                            <Flex key={event.key} alignItems="flex-start" style={{ position: 'relative' }}>
                                {/* Vertical line - only show if not the last event */}
                                {!isLastEvent && (
                                    <div className={styles.timelineLine} />
                                )}

                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: NEGATIVE_STAGES.includes(stageValue) ? '#EF4444' : '#10B981',
                                        color: '#fff',
                                        position: 'absolute',
                                        left: -48,
                                    }}
                                >
                                    {STAGE_ICONS[stageValue as ELeadStage] ?? UNKNOWN_STAGE_ICON}
                                </Avatar>

                                <FlexColumn gap={0} sx={{ minWidth: 0 }}>
                                    <div className={styles.eventLabel}>
                                        {label}
                                    </div>

                                    <Tooltip title={details}>
                                        <Typography className={clsx(styles.eventDetails, 'ellipsisText')} variant="body2" color="textSecondary">
                                            {details}
                                        </Typography>
                                    </Tooltip>

                                    <Flex alignItems="center" className={styles.eventTimestamp}>
                                        <Calendar size={16} color="#6B7280" />
                                        {timeValue}
                                        <OriginPill origin={origin} label={originLabel} details={originDetails} />
                                    </Flex>
                                </FlexColumn>
                            </Flex>
                        );
                    })}
                </FlexColumn>
            </div>
        </ExpandableItem>
    );
}
